import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Table, 
  Badge, 
  Alert 
} from 'react-bootstrap';
import { 
  FaProjectDiagram, 
  FaSync, 
  FaStop, 
  FaEye, 
  FaExclamationTriangle,
  FaPlayCircle,
  FaInfoCircle,
  FaChevronDown,
  FaChevronRight,
  FaRobot,
  FaCog,
  FaServer
} from 'react-icons/fa';
import Spinner from '../components/Spinner';
import * as api from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AgentStructurePage.css';

// Helper function to safely convert any value to string for rendering
const safeString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    // If it's an object with micro_agent_number or name, extract those
    if (value.micro_agent_number !== undefined) return String(value.micro_agent_number);
    if (value.name !== undefined) return String(value.name);
    if (value.id !== undefined) return String(value.id);
    // Otherwise return empty string to avoid rendering [object Object]
    return '';
  }
  return String(value);
};

const TreeNode = ({ 
  node, 
  level = 0, 
  isLast = false, 
  parentLines = [], 
  onNodeClick,
  expandedNodes,
  onToggleExpand 
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes[node.id] !== false; // Default to expanded
  const indentLevel = level * 24;

  const getNodeIcon = (nodeType) => {
    switch (nodeType) {
      case 'mini': return <FaRobot className="node-icon mini-icon" />;
      case 'micro': return <FaCog className="node-icon micro-icon" />;
      case 'service': return <FaServer className="node-icon service-icon" />;
      case 'special': return <FaProjectDiagram className="node-icon special-icon" />;
      default: return <FaRobot className="node-icon" />;
    }
  };

  const getStatusBadge = (nodeType) => {
    const statusMap = {
      'mini': { bg: 'primary', text: 'Mini' },
      'micro': { bg: 'info', text: 'Micro' },
      'service': { bg: 'secondary', text: 'Service' },
      'special': { bg: 'warning', text: 'Special' }
    };
    const status = statusMap[nodeType] || { bg: 'dark', text: 'Unknown' };
    return <Badge bg={status.bg} className="ms-2 node-badge">{status.text}</Badge>;
  };

  const renderTreeLines = () => {
    const lines = [];
    
    // Render parent lines
    parentLines.forEach((hasLine, idx) => {
      if (hasLine) {
        lines.push(
          <span 
            key={`parent-${idx}`} 
            className="tree-line vertical" 
            style={{ left: `${idx * 24 + 12}px` }}
          />
        );
      }
    });

    // Render current level line
    if (level > 0) {
      lines.push(
        <span 
          key="current-horizontal" 
          className="tree-line horizontal" 
          style={{ left: `${(level - 1) * 24 + 12}px` }}
        />
      );
      
      if (!isLast) {
        lines.push(
          <span 
            key="current-vertical" 
            className="tree-line vertical" 
            style={{ left: `${(level - 1) * 24 + 12}px` }}
          />
        );
      }
    }

    return lines;
  };

  // CRITICAL FIX: Ensure node.name is always rendered as a string
  const nodeName = safeString(node.name);
  const nodeDisplayName = safeString(node.displayName);

  return (
    <div className="tree-node-container">
      <div className="tree-node-wrapper" style={{ paddingLeft: `${indentLevel}px` }}>
        {renderTreeLines()}
        
        <div 
          className={`tree-node-item ${node.type}`}
          onClick={() => onNodeClick && onNodeClick(node)}
        >
          <div className="tree-node-content">
            {hasChildren && (
              <button
                className="expand-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(node.id);
                }}
              >
                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
              </button>
            )}
            
            {!hasChildren && <span className="expand-spacer" />}
            
            {getNodeIcon(node.type)}
            
            <div className="node-info">
              <span className="node-name">
                <span className="node-number">{nodeName}</span>
                {nodeDisplayName && (
                  <span className="node-display-name"> - {nodeDisplayName}</span>
                )}
              </span>
              {getStatusBadge(node.type)}
            </div>
          </div>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="tree-children">
          {node.children.map((child, index) => {
            const isLastChild = index === node.children.length - 1;
            const newParentLines = [...parentLines];
            if (level >= 0) {
              newParentLines[level] = !isLast;
            }
            
            return (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                isLast={isLastChild}
                parentLines={newParentLines}
                onNodeClick={onNodeClick}
                expandedNodes={expandedNodes}
                onToggleExpand={onToggleExpand}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const VerticalTreeVisualization = ({ treeData }) => {
  const [expandedNodes, setExpandedNodes] = useState({});

  const handleToggleExpand = useCallback((nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  }, []);

  const handleNodeClick = useCallback((node) => {
    console.log('Node clicked:', node);
    // Add navigation logic here if needed
  }, []);

  if (!treeData) {
    return null;
  }

  const { metadata, associations } = treeData;

  // Check if we have any data to display
  const hasAssociations = associations && 
    ((associations.mini_to_micro_agent && associations.mini_to_micro_agent.length > 0) ||
     (associations.mini_to_micro_service && associations.mini_to_micro_service.length > 0));

  if (!hasAssociations && !metadata?.conversation_micro_agent && !metadata?.master_mini_agent) {
    return null;
  }

  // Build tree structure
  const buildTreeStructure = () => {
    const nodes = new Map();
    const childrenByParent = new Map();
    const allNodeIds = new Set();
    const childNodeIds = new Set();

    // CRITICAL FIX: Helper function to extract agent info and ensure strings
    const extractAgentInfo = (agentData) => {
      // Handle null/undefined
      if (!agentData) {
        return { id: '', displayName: null };
      }
      
      // Handle string format
      if (typeof agentData === 'string') {
        return { id: agentData, displayName: null };
      }
      
      // Handle object format with micro_agent_number and name
      if (typeof agentData === 'object') {
        // Extract ID - prioritize micro_agent_number, then id, then convert whole object
        let idValue = agentData.micro_agent_number || agentData.id;
        
        // CRITICAL: Ensure idValue is a string, never an object
        if (idValue !== undefined && idValue !== null) {
          idValue = String(idValue);
        } else {
          // Fallback: try to stringify but avoid [object Object]
          idValue = agentData.micro_agent_number ? String(agentData.micro_agent_number) : 
                    agentData.name ? String(agentData.name) : 
                    'unknown';
        }
        
        // Extract display name
        const displayName = agentData.name ? String(agentData.name) : null;
        
        return {
          id: idValue,
          displayName: displayName
        };
      }
      
      // Fallback for other types
      return { id: String(agentData), displayName: null };
    };

    // Initialize nodes from metadata
    if (metadata?.conversation_micro_agent) {
      const convId = 'conversation_agent';
      const convValue = safeString(metadata.conversation_micro_agent);
      nodes.set(convId, {
        id: convId,
        name: convValue,
        displayName: null,
        type: 'special',
        children: []
      });
      allNodeIds.add(convId);
    }

    if (metadata?.master_mini_agent) {
      const masterId = 'master_mini_agent';
      const masterValue = safeString(metadata.master_mini_agent);
      nodes.set(masterId, {
        id: masterId,
        name: masterValue,
        displayName: null,
        type: 'mini',
        children: []
      });
      allNodeIds.add(masterId);
    }

    // Process associations
    if (associations) {
      // Process mini to micro agent relationships
      if (associations.mini_to_micro_agent) {
        associations.mini_to_micro_agent.forEach(assoc => {
          const parentInfo = extractAgentInfo(assoc.parent_mini_agent);
          const childInfo = extractAgentInfo(assoc.child_micro_agent);
          
          const parentId = parentInfo.id;
          const childId = childInfo.id;
          
          // Skip if we couldn't extract valid IDs
          if (!parentId || !childId) {
            console.warn('[AgentStructure] Skipping association with invalid IDs:', assoc);
            return;
          }
          
          allNodeIds.add(parentId);
          allNodeIds.add(childId);
          childNodeIds.add(childId);

          // Create parent node if it doesn't exist
          if (!nodes.has(parentId)) {
            nodes.set(parentId, {
              id: parentId,
              name: parentId,
              displayName: parentInfo.displayName,
              type: 'mini',
              children: []
            });
          } else {
            // Update displayName if we have it and node doesn't
            const existingNode = nodes.get(parentId);
            if (parentInfo.displayName && !existingNode.displayName) {
              existingNode.displayName = parentInfo.displayName;
            }
          }

          // Create child node if it doesn't exist
          if (!nodes.has(childId)) {
            // Determine if child is mini or micro based on whether it appears as parent elsewhere
            const isMiniAgent = associations.mini_to_micro_agent.some(a => {
              const checkParentInfo = extractAgentInfo(a.parent_mini_agent);
              return checkParentInfo.id === childId;
            }) || associations.mini_to_micro_service?.some(a => {
              const checkParentInfo = extractAgentInfo(a.parent_mini_agent);
              return checkParentInfo.id === childId;
            });
            
            nodes.set(childId, {
              id: childId,
              name: childId,
              displayName: childInfo.displayName,
              type: isMiniAgent ? 'mini' : 'micro',
              children: []
            });
          } else {
            // Update displayName if we have it and node doesn't
            const existingNode = nodes.get(childId);
            if (childInfo.displayName && !existingNode.displayName) {
              existingNode.displayName = childInfo.displayName;
            }
          }

          // Add to children map
          if (!childrenByParent.has(parentId)) {
            childrenByParent.set(parentId, []);
          }
          childrenByParent.get(parentId).push(childId);
        });
      }

      // Process mini to micro service relationships
      if (associations.mini_to_micro_service) {
        associations.mini_to_micro_service.forEach(assoc => {
          const parentInfo = extractAgentInfo(assoc.parent_mini_agent);
          const parentId = parentInfo.id;
          const serviceId = safeString(assoc.child_micro_service);
          
          // Skip if invalid
          if (!parentId || !serviceId) {
            console.warn('[AgentStructure] Skipping service association with invalid IDs:', assoc);
            return;
          }
          
          allNodeIds.add(parentId);

          // Create parent node if it doesn't exist
          if (!nodes.has(parentId)) {
            nodes.set(parentId, {
              id: parentId,
              name: parentId,
              displayName: parentInfo.displayName,
              type: 'mini',
              children: []
            });
          } else {
            // Update displayName if we have it and node doesn't
            const existingNode = nodes.get(parentId);
            if (parentInfo.displayName && !existingNode.displayName) {
              existingNode.displayName = parentInfo.displayName;
            }
          }

          // Create service node
          const serviceNodeId = `service_${serviceId}`;
          nodes.set(serviceNodeId, {
            id: serviceNodeId,
            name: serviceId,
            displayName: null,
            type: 'service',
            children: []
          });

          // Add to children map
          if (!childrenByParent.has(parentId)) {
            childrenByParent.set(parentId, []);
          }
          childrenByParent.get(parentId).push(serviceNodeId);
        });
      }
    }

    // Build tree hierarchy
    const buildChildren = (nodeId) => {
      const node = nodes.get(nodeId);
      if (!node) return;

      const children = childrenByParent.get(nodeId) || [];
      node.children = children.map(childId => {
        buildChildren(childId);
        return nodes.get(childId);
      }).filter(Boolean);
    };

    // Find root nodes (nodes that are not children of any other node)
    const rootNodeIds = Array.from(allNodeIds).filter(nodeId => !childNodeIds.has(nodeId));
    
    // If we have a conversation agent, it should be the root
    if (metadata?.conversation_micro_agent) {
      const convId = 'conversation_agent';
      if (metadata?.master_mini_agent) {
        const masterId = 'master_mini_agent';
        if (!childrenByParent.has(convId)) {
          childrenByParent.set(convId, []);
        }
        childrenByParent.get(convId).push(masterId);
      }
      rootNodeIds.length = 0;
      rootNodeIds.push(convId);
    } else if (metadata?.master_mini_agent && rootNodeIds.length > 1) {
      // If we have a master mini agent and multiple roots, make master the root
      const masterId = 'master_mini_agent';
      const otherRoots = rootNodeIds.filter(id => id !== masterId);
      if (otherRoots.length > 0) {
        if (!childrenByParent.has(masterId)) {
          childrenByParent.set(masterId, []);
        }
        childrenByParent.get(masterId).push(...otherRoots);
        rootNodeIds.length = 0;
        rootNodeIds.push(masterId);
      }
    }

    // Build the tree structure
    rootNodeIds.forEach(rootId => buildChildren(rootId));

    return rootNodeIds.map(rootId => nodes.get(rootId)).filter(Boolean);
  };

  const treeRoots = buildTreeStructure();

  if (treeRoots.length === 0) {
    return null;
  }

  return (
    <div className="vertical-tree-container">
      <div className="tree-content">
        {treeRoots.map((root, index) => (
          <TreeNode
            key={root.id}
            node={root}
            level={0}
            isLast={index === treeRoots.length - 1}
            parentLines={[]}
            onNodeClick={handleNodeClick}
            expandedNodes={expandedNodes}
            onToggleExpand={handleToggleExpand}
          />
        ))}
      </div>
    </div>
  );
};

const AgentStructurePage = () => {
  const navigate = useNavigate();

  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("1");
  const [treeData, setTreeData] = useState(null);
  const [pendingExecutions, setPendingExecutions] = useState([]);
  const [orphans, setOrphans] = useState({ micro_agents: [], micro_services: [] });
  
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [error, setError] = useState(null);
  const [stoppingExecution, setStoppingExecution] = useState({});

  const [debugInfo, setDebugInfo] = useState({
    dataLoaded: false,
    nodesCount: 0,
    renderStatus: 'INIT',
    hasAssociations: false,
    rootNodesCount: 0
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (selectedAgentId) {
      fetchTreeData(selectedAgentId);
      fetchPendingExecutions(selectedAgentId);
    }
  }, [selectedAgentId]);

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await api.getAgents();
      if (response && response.agents) {
        setAgents(response.agents);
        console.log('[AgentStructure] Loaded agents:', response.agents.length);
      }
    } catch (err) {
      console.error('[AgentStructure] Error fetching agents:', err);
      toast.error('Failed to load agents list');
    } finally {
      setLoadingAgents(false);
    }
  };

  const fetchTreeData = async (agentId) => {
    try {
      setLoadingTree(true);
      setError(null);
      setDebugInfo(prev => ({ ...prev, renderStatus: 'LOADING' }));
      
      console.log('[AgentStructure] Fetching tree data for agent:', agentId);
      const response = await api.getMiniAgentTree(agentId);
      
      console.log('[AgentStructure] Raw API response:', response);
      
      if (response && response.success && response.data) {
        setTreeData(response.data);
        
        // Count nodes and associations
        let nodeCount = 0;
        let rootNodesCount = 0;
        let hasAssociations = false;
        
        if (response.data.metadata) {
          if (response.data.metadata.master_mini_agent) nodeCount++;
          if (response.data.metadata.conversation_micro_agent) nodeCount++;
        }
        
        if (response.data.associations) {
          const allNodes = new Set();
          const childNodes = new Set();
          
          if (response.data.associations.mini_to_micro_agent) {
            hasAssociations = true;
            nodeCount += response.data.associations.mini_to_micro_agent.length;
            
            response.data.associations.mini_to_micro_agent.forEach(assoc => {
              const extractId = (data) => {
                if (!data) return '';
                if (typeof data === 'string') return data;
                if (data && typeof data === 'object') {
                  return String(data.micro_agent_number || data.id || data.name || '');
                }
                return String(data);
              };
              
              const parentId = extractId(assoc.parent_mini_agent);
              const childId = extractId(assoc.child_micro_agent);
              
              if (parentId) allNodes.add(parentId);
              if (childId) {
                allNodes.add(childId);
                childNodes.add(childId);
              }
            });
          }
          
          if (response.data.associations.mini_to_micro_service) {
            hasAssociations = true;
            nodeCount += response.data.associations.mini_to_micro_service.length;
            
            response.data.associations.mini_to_micro_service.forEach(assoc => {
              const extractId = (data) => {
                if (!data) return '';
                if (typeof data === 'string') return data;
                if (data && typeof data === 'object') {
                  return String(data.micro_agent_number || data.id || data.name || '');
                }
                return String(data);
              };
              
              const parentId = extractId(assoc.parent_mini_agent);
              if (parentId) allNodes.add(parentId);
            });
          }
          
          // Count root nodes (nodes that are never children)
          rootNodesCount = Array.from(allNodes).filter(node => !childNodes.has(node)).length;
        }
        
        setDebugInfo({
          dataLoaded: true,
          nodesCount: nodeCount,
          renderStatus: hasAssociations || nodeCount > 0 ? 'RENDERED' : 'NO_DATA',
          hasAssociations,
          rootNodesCount
        });
        
        if (response.data.orphans) {
          setOrphans(response.data.orphans);
        }
        
        console.log('[AgentStructure] Tree data loaded - Nodes:', nodeCount, 'Roots:', rootNodesCount, 'HasAssoc:', hasAssociations);
      } else {
        if (response && response.success) {
           setTreeData(response.data || {});
           setOrphans(response.data?.orphans || { micro_agents: [], micro_services: [] });
           setDebugInfo({
             dataLoaded: true,
             nodesCount: 0,
             renderStatus: 'NO_DATA',
             hasAssociations: false,
             rootNodesCount: 0
           });
        } else {
           throw new Error('Invalid response structure');
        }
      }
    } catch (err) {
      console.error('[AgentStructure] Error fetching agent tree:', err);
      setError('Failed to load agent structure. Please try again.');
      setDebugInfo(prev => ({ ...prev, renderStatus: 'ERROR' }));
    } finally {
      setLoadingTree(false);
    }
  };

  const fetchPendingExecutions = async (agentId) => {
    try {
      setLoadingExecutions(true);
      const response = await api.getAgentPendingExecutions(agentId);
      
      if (response && response.success && response.pending_executions) {
        setPendingExecutions(response.pending_executions);
      } else {
        setPendingExecutions([]);
      }
    } catch (err) {
      console.error('[AgentStructure] Error fetching pending executions:', err);
      toast.warn('Failed to load pending executions');
    } finally {
      setLoadingExecutions(false);
    }
  };

  const handleAgentChange = (e) => {
    setSelectedAgentId(e.target.value);
  };

  const handleRefresh = () => {
    if (selectedAgentId) {
      fetchTreeData(selectedAgentId);
      fetchPendingExecutions(selectedAgentId);
    }
  };

  // NEW ROUTING LOGIC: Route based on first digit of micro_agent_number
  const handleViewExecution = (execution) => {
    const { 
      micro_agent_id, 
      micro_agent_execution_id, 
      payload, 
      execution_arn, 
      created_at, 
      outcome, 
      agent_output, 
      execution_log, 
      micro_agent_number, 
      micro_agent_name 
    } = execution;
    
    const executionContext = {
      payload: payload,
      environment: execution_arn?.includes(':PROD') ? 'production' : 'development',
      executionArn: execution_arn,
      executionId: micro_agent_execution_id,
      timestamp: created_at,
      executionStatus: {
        status: outcome === 'RUNNING' ? 'RUNNING' : (outcome || 'UNKNOWN').toUpperCase(),
        startDate: created_at,
        duration: execution.duration_seconds
      },
      executionResult: {
        agentOutput: agent_output,
        executionLog: execution_log,
        outcome: outcome
      },
      liveLogs: execution_log,
      fromLogsReview: true,
      metadata: {
        microAgentId: micro_agent_id,
        executionId: micro_agent_execution_id,
        createdAt: created_at,
        microAgentNumber: micro_agent_number,
        microAgentName: micro_agent_name
      }
    };
    
    localStorage.setItem('retryExecutionPayload', JSON.stringify(executionContext));
    
    // CRITICAL FIX: Use safeString to extract number/name
    const numberAsString = safeString(micro_agent_number).trim();
    const nameAsString = safeString(micro_agent_name).trim().toLowerCase();
    
    const isMiniAgent = 
      numberAsString.charAt(0) === '9' ||
      numberAsString.startsWith('9') ||
      parseInt(numberAsString, 10) >= 900 ||
      nameAsString.includes('mini');
    
    if (isMiniAgent) {
      navigate(`/agents/${selectedAgentId}/mini-agents/${micro_agent_id}/testing`);
    } else {
      navigate(`/agents/${selectedAgentId}/micro-agents/${micro_agent_id}/testing`);
    }
  };

  const handleStopExecution = async (execution) => {
    const executionArn = execution.execution_arn;
    if (!executionArn) return;

    try {
      setStoppingExecution(prev => ({ ...prev, [executionArn]: true }));
      const response = await api.makeAuthenticatedRequest('POST', '/execution/stop-step-function', {
        executionArn: executionArn
      });
      
      if (response && response.success) {
        toast.success('Execution stopped successfully');
        fetchPendingExecutions(selectedAgentId);
      } else {
        throw new Error('Failed to stop execution');
      }
    } catch (err) {
      console.error('[AgentStructure] Error stopping execution:', err);
      toast.error('Failed to stop execution: ' + err.message);
    } finally {
      setStoppingExecution(prev => ({ ...prev, [executionArn]: false }));
    }
  };

  const renderTreeVisualization = () => {
    if (loadingTree) {
      return (
        <div className="structure-placeholder loading">
          <Spinner />
          <p className="mt-3">Loading agent tree structure...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="structure-placeholder error">
          <Alert variant="danger">
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
          <Button variant="outline-primary" size="sm" onClick={handleRefresh} className="mt-3">
            <FaSync className="me-2" />
            Retry Loading
          </Button>
        </div>
      );
    }

    if (!treeData) {
      return (
        <div className="structure-placeholder empty">
          <FaProjectDiagram size={64} className="text-muted mb-3" />
          <h5 className="text-muted">No Agent Tree Data Available</h5>
          <p className="text-muted">
            Select an agent and click refresh to load the tree structure.
          </p>
          <Button variant="outline-primary" size="sm" onClick={handleRefresh} className="mt-3">
            <FaSync className="me-2" />
            Load Tree Data
          </Button>
        </div>
      );
    }

    // Check if we have any displayable data
    const hasAssociations = treeData.associations && 
      ((treeData.associations.mini_to_micro_agent && treeData.associations.mini_to_micro_agent.length > 0) ||
       (treeData.associations.mini_to_micro_service && treeData.associations.mini_to_micro_service.length > 0));
    
    const hasMetadata = treeData.metadata && 
      (treeData.metadata.master_mini_agent || treeData.metadata.conversation_micro_agent);

    if (!hasAssociations && !hasMetadata) {
      return (
        <div className="structure-placeholder empty">
          <FaProjectDiagram size={64} className="text-muted mb-3" />
          <h5 className="text-muted">No Agent Structure Found</h5>
          <p className="text-muted">
            This agent has no associated mini-agents or micro-agents configured yet.
          </p>
          <div className="mt-3">
            <small className="text-muted d-block mb-2">
              Metadata: {hasMetadata ? 'Present' : 'Empty'} | 
              Associations: {hasAssociations ? 'Present' : 'Empty'}
            </small>
            <Button variant="outline-primary" size="sm" onClick={handleRefresh}>
              <FaSync className="me-2" />
              Refresh
            </Button>
          </div>
        </div>
      );
    }

    try {
      return <VerticalTreeVisualization treeData={treeData} />;
    } catch (err) {
      console.error('[AgentStructure] Tree render failed:', err);
      return (
        <div className="structure-placeholder error">
          <Alert variant="danger">
            <FaExclamationTriangle className="me-2" />
            Failed to render tree visualization: {err.message}
          </Alert>
          <Button variant="outline-primary" size="sm" onClick={handleRefresh} className="mt-3">
            <FaSync className="me-2" />
            Retry
          </Button>
        </div>
      );
    }
  };

  return (
    <Container fluid className="agent-structure-page py-4">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="page-title">
              <FaProjectDiagram className="me-3" />
              Agent Structure
            </h1>
            <p className="page-description text-muted">
              Visualize agent hierarchy and manage executions
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={handleRefresh} disabled={loadingTree || loadingExecutions}>
              <FaSync className={loadingTree ? 'fa-spin me-2' : 'me-2'} />
              Refresh Data
            </Button>
          </div>
        </div>
      </div>

      <div className="debug-info-panel mb-3">
        <small className="text-muted d-flex align-items-center gap-2">
          <FaInfoCircle />
          <span>
            Data Loaded: <strong>{debugInfo.dataLoaded ? 'YES' : 'NO'}</strong> | 
            Total Nodes: <strong>{debugInfo.nodesCount}</strong> | 
            Root Nodes: <strong>{debugInfo.rootNodesCount}</strong> | 
            Has Associations: <strong>{debugInfo.hasAssociations ? 'YES' : 'NO'}</strong> | 
            Status: <strong>{debugInfo.renderStatus}</strong>
          </span>
        </small>
      </div>

      <Card className="mb-4 selection-card">
        <Card.Body>
          <Form.Group as={Row} className="align-items-center">
            <Form.Label column sm={2} className="fw-bold">Select Agent:</Form.Label>
            <Col sm={6}>
              <Form.Select 
                value={selectedAgentId} 
                onChange={handleAgentChange}
                disabled={loadingTree}
              >
                {agents.map(agent => (
                  <option key={agent.uniqueIdentifier} value={agent.uniqueIdentifier}>
                    {agent.agentName || agent.repoName || `Agent ${agent.uniqueIdentifier}`}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4 executions-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FaPlayCircle className="me-2 text-success" /> 
            Pending Executions 
            {pendingExecutions.length > 0 && <Badge bg="danger" className="ms-2">{pendingExecutions.length}</Badge>}
          </h5>
          {loadingExecutions && <Spinner size="sm" />}
        </Card.Header>
        <Card.Body>
          {pendingExecutions.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <p>No pending executions found.</p>
            </div>
          ) : (
            <Table responsive hover className="align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Micro-Agent</th>
                  <th>Execution ARN</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingExecutions.map(exec => (
                  <tr key={exec.micro_agent_execution_id}>
                    <td>{exec.micro_agent_execution_id}</td>
                    <td>
                      <div><strong>{safeString(exec.micro_agent_number)}</strong></div>
                      <small className="text-muted">{safeString(exec.micro_agent_name)}</small>
                    </td>
                    <td>
                      <small className="text-monospace" title={exec.execution_arn}>
                        {exec.execution_arn.split(':').pop().substring(0, 20)}...
                      </small>
                    </td>
                    <td>
                      <Badge bg={exec.outcome === 'RUNNING' ? 'primary' : 'secondary'} className="status-badge-running">
                        {exec.outcome || 'UNKNOWN'}
                      </Badge>
                    </td>
                    <td>{new Date(exec.created_at).toLocaleString()}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="outline-info" 
                          size="sm" 
                          title="View Execution Details"
                          onClick={() => handleViewExecution(exec)}
                        >
                          <FaEye />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          title="Stop Execution"
                          onClick={() => handleStopExecution(exec)}
                          disabled={stoppingExecution[exec.execution_arn]}
                        >
                          {stoppingExecution[exec.execution_arn] ? <Spinner size="sm"/> : <FaStop />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Row className="structure-row">
        <Col lg={9} className="structure-col">
          <Card className="structure-card">
            <Card.Header className="structure-card-header">
              <h5 className="mb-0">
                <FaProjectDiagram className="me-2" />
                Agent Tree Structure
              </h5>
            </Card.Header>
            <Card.Body className="structure-card-body">
              {renderTreeVisualization()}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3}>
          <Card className="orphans-card h-100">
            <Card.Header>
              <h5 className="mb-0">
                <FaExclamationTriangle className="me-2 text-warning" />
                Orphans
              </h5>
            </Card.Header>
            <Card.Body>
              <h6 className="text-primary mt-2">Micro-Agents</h6>
              {orphans.micro_agents && orphans.micro_agents.length > 0 ? (
                <div className="orphan-list mb-3">
                  {orphans.micro_agents.map((id, idx) => (
                    <Badge key={idx} bg="secondary" className="me-1 mb-1">{safeString(id)}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted small">No orphan micro-agents</p>
              )}

              <hr />

              <h6 className="text-info">Micro-Services</h6>
              {orphans.micro_services && orphans.micro_services.length > 0 ? (
                <div className="orphan-list">
                  {orphans.micro_services.map((id, idx) => (
                    <Badge key={idx} bg="dark" text="light" className="me-1 mb-1">{safeString(id)}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted small">No orphan micro-services</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AgentStructurePage;